class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.messageEl = this.querySelector('.free-shipping-bar__message');
    this.fillEl = this.querySelector('.free-shipping-bar__fill');
    this.iconEl = this.querySelector('.free-shipping-bar__icon');
    this.threshold = Number(this.dataset.threshold || 0);
    this.moneyFormat = this.decodeHtml(this.dataset.moneyFormat || '£{{amount}}');
    this.progressTemplate = this.decodeHtml(
      this.dataset.progressTemplate || 'Spend |||AMOUNT||| more to unlock free delivery'
    );
    this.unlockedText = this.decodeHtml(
      this.dataset.unlockedText || 'Congratulations! You have free shipping'
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }

    const drawer = this.closest('cart-drawer');
    const target = this.getProgressValue(this.dataset.progress);
    const previous = drawer ? this.getProgressValue(drawer.dataset.fsbProgress) : 0;

    // Always paint an initial width so the white track + black fill are visible
    this.setProgress(previous || 0, false);

    if (drawer && !drawer.classList.contains('active')) {
      return;
    }

    this.animateProgress(target, { from: previous });
  }

  disconnectedCallback() {
    this.unsubscribeCart?.();
    window.clearTimeout(this._animTimer);
  }

  decodeHtml(value) {
    if (!value) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  onCartUpdate(event) {
    const cartData = event?.cartData;
    if (!cartData || typeof cartData.total_price !== 'number') return;
    this.updateFromTotal(cartData.total_price);
  }

  getProgressValue(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  }

  updateFromTotal(totalCents) {
    if (!this.threshold) return;

    const remaining = Math.max(this.threshold - totalCents, 0);
    const progress = Math.max(0, Math.min(100, (totalCents / this.threshold) * 100));
    const unlocked = remaining <= 0;

    this.dataset.total = String(totalCents);
    this.dataset.progress = String(progress);
    this.classList.toggle('free-shipping-bar--unlocked', unlocked);

    if (this.messageEl) {
      const nextText = unlocked
        ? this.unlockedText
        : this.progressTemplate.replace('|||AMOUNT|||', this.formatMoney(remaining));

      if (this.messageEl.textContent.trim() !== nextText.trim()) {
        this.messageEl.textContent = nextText;
        this.messageEl.classList.remove('is-updating');
        void this.messageEl.offsetWidth;
        this.messageEl.classList.add('is-updating');
      }
    }

    this.animateProgress(progress);
  }

  setProgress(percent, animate = true) {
    const clamped = this.getProgressValue(percent);
    const value = `${clamped}%`;

    if (this.fillEl) {
      if (!animate) this.fillEl.style.transition = 'none';
      this.fillEl.style.width = value;
      if (!animate) {
        void this.fillEl.offsetWidth;
        this.fillEl.style.transition = '';
      }
    }

    if (this.iconEl) {
      if (!animate) this.iconEl.style.transition = 'none';
      this.iconEl.style.left = value;
      if (!animate) {
        void this.iconEl.offsetWidth;
        this.iconEl.style.transition = '';
      }
    }

    const drawer = this.closest('cart-drawer');
    if (drawer) drawer.dataset.fsbProgress = String(clamped);
  }

  animateProgress(target, { from } = {}) {
    const clamped = this.getProgressValue(target != null ? target : this.dataset.progress);
    const start =
      typeof from === 'number'
        ? this.getProgressValue(from)
        : this.getProgressValue(parseFloat(this.fillEl?.style.width) || 0);

    this.classList.remove('is-animating');

    // Start from known value, then animate to target on next frame
    this.setProgress(start, false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setProgress(clamped, true);
        this.classList.add('is-animating');
      });
    });

    window.clearTimeout(this._animTimer);
    this._animTimer = window.setTimeout(() => {
      this.classList.remove('is-animating');
    }, 750);
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, this.moneyFormat).replace(/<[^>]*>/g, '').trim();
    }

    const format = this.moneyFormat || '£{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    const value = (Number(cents) / 100).toFixed(2);
    const valueNoDecimals = String(Math.round(Number(cents) / 100));

    if (!match) return `£${value}`;

    const token = match[1];
    const formatted =
      token === 'amount_no_decimals' || token === 'amount_no_decimals_with_comma_separator'
        ? valueNoDecimals
        : value;

    return format.replace(placeholder, formatted);
  }
}

customElements.define('free-shipping-bar', FreeShippingBar);
